
import Component, { mixins } from 'vue-class-component';
import { Emit, Prop, Watch } from 'vue-property-decorator';
import FormValidationMixin from '@/mixins/FormValidationMixin';
import FormInput from '@/components/form/FormInput.vue';
import { Validations } from 'vuelidate-property-decorators';
import { maxLength, minLength, required } from 'vuelidate/lib/validators';
import { ProfileFormData, ProfileResponse } from '@/model/Profile';
import ToastMixin from '@/mixins/ToastMixin';
import isEqual from 'lodash.isequal';

@Component({
    components: {
        FormInput,
    },
})
export default class ProfileForm extends mixins(
    FormValidationMixin,
    ToastMixin,
) {
    // Java's Integer.MAX_VALUE
    readonly chatbotTokenLimitMax = 2147483647;

    @Prop() profileResponse!: ProfileResponse;

    @Prop({ default: false }) readonly showChatbotQuotaFields!: boolean;

    formData = {} as ProfileFormData;

    initialFormData = {} as ProfileFormData;

    submitted = false;

    @Watch('profileResponse', { immediate: true })
    watch(): void {
        const { normalizedProfileResponse } = this;
        this.formData = { ...normalizedProfileResponse };
        this.initialFormData = { ...normalizedProfileResponse };
    }

    @Watch('formData.chatbotEnabled')
    onChatbotEnabledChange(chatbotEnabled: boolean): void {
        if (chatbotEnabled === false) {
            this.formData.chatbotTokenLimitMonthly =
                this.normalizedProfileResponse.chatbotTokenLimitMonthly ?? 0;
        }
    }

    get isFormDataSameAsInitialData(): boolean {
        return isEqual(
            this.createComparableProfileData(this.initialFormData),
            this.createComparableProfileData(this.formData),
        );
    }

    get normalizedProfileResponse(): ProfileFormData {
        return this.showChatbotQuotaFields
            ? {
                  ...this.profileResponse,
                  chatbotEnabled: this.profileResponse.chatbotEnabled !== false,
              }
            : { ...this.profileResponse };
    }

    get chatbotTokenLimitValidation(): Record<string, boolean> {
        return this.$v.formData.chatbotTokenLimitMonthly;
    }

    get chatbotTokensUsedMonthly(): number {
        return this.formData.chatbotTokensUsedMonthly ?? 0;
    }

    get chatbotTokenLimitInput(): string {
        return this.formData.chatbotTokenLimitMonthly?.toString() ?? '';
    }

    set chatbotTokenLimitInput(value: string) {
        const digitsOnly = value.replaceAll(/\D/g, '');

        this.formData.chatbotTokenLimitMonthly = digitsOnly.length
            ? Number(digitsOnly)
            : undefined;
    }

    onChatbotTokenLimitKeydown(event: KeyboardEvent): void {
        if (
            event.ctrlKey ||
            event.metaKey ||
            event.altKey ||
            event.key.length !== 1 ||
            /^\d$/.test(event.key)
        ) {
            return;
        }

        event.preventDefault();
    }

    onChatbotTokenLimitPaste(event: ClipboardEvent): void {
        const pastedText = event.clipboardData?.getData('text') ?? '';
        const pastedDigits = pastedText.replaceAll(/\D/g, '');

        if (!pastedDigits) {
            return;
        }

        const input = event.target as HTMLInputElement;
        const selectionStart = input.selectionStart ?? input.value.length;
        const selectionEnd = input.selectionEnd ?? input.value.length;

        this.chatbotTokenLimitInput = `${input.value.slice(
            0,
            selectionStart,
        )}${pastedDigits}${input.value.slice(selectionEnd)}`;
    }

    get isFormChatbotEnabled(): boolean {
        return this.formData.chatbotEnabled !== false;
    }

    get chatbotTokenLimitState(): boolean | null {
        if (this.hasChatbotTokenLimitBackendValidationMessages) {
            return false;
        }

        return this.isFormChatbotEnabled
            ? this.getState('chatbotTokenLimitMonthly')
            : null;
    }

    get isChatbotTokenLimitRequiredInvalid(): boolean {
        return (
            this.submitted && !this.chatbotTokenLimitValidation.requiredIfAdmin
        );
    }

    get isChatbotTokenLimitIntegerInvalid(): boolean {
        return this.submitted && !this.chatbotTokenLimitValidation.integer;
    }

    get isChatbotTokenLimitMinInvalid(): boolean {
        return this.submitted && !this.chatbotTokenLimitValidation.minValue;
    }

    get isChatbotTokenLimitMaxInvalid(): boolean {
        return this.submitted && !this.chatbotTokenLimitValidation.maxValue;
    }

    get isChatbotTokenLimitUsedMinInvalid(): boolean {
        return this.submitted && !this.chatbotTokenLimitValidation.usedMinValue;
    }

    get hasChatbotTokenLimitBackendValidationMessages(): boolean {
        return this.chatbotTokenLimitBackendValidationMessages.length > 0;
    }

    get chatbotTokenLimitBackendValidationMessages(): string[] {
        const validationMessages =
            this.error?.validationMessages?.chatbotTokenLimitMonthly;

        if (!validationMessages) {
            return [];
        }

        const messages =
            typeof validationMessages === 'string'
                ? [validationMessages]
                : validationMessages;

        return messages.map((message) => {
            if (message === 'validation.chatbot-token-limit.used-min') {
                return this.$t(message, {
                    used: this.chatbotTokensUsedMonthly,
                }).toString();
            }

            return this.$t(message).toString();
        });
    }

    @Validations()
    validations = {
        formData: {
            firstName: {
                required,
                maxLength: maxLength(20),
            },
            lastName: {
                required,
                maxLength: maxLength(20),
            },
            phone: {
                required,
                minLength: minLength(6),
                maxLength: maxLength(15),
                phoneNumber: (value: string | undefined): boolean => {
                    return value?.length
                        ? /^[+]{0,1}[-\s0-9]*$/.test(value)
                        : true;
                },
            },
            chatbotTokenLimitMonthly: {
                requiredIfAdmin: (value: number | null | undefined): boolean =>
                    !this.showChatbotQuotaFields ||
                    !this.isFormChatbotEnabled ||
                    value === 0 ||
                    (value !== undefined && value !== null),
                integer: (value: number | null | undefined): boolean => {
                    return (
                        !this.showChatbotQuotaFields ||
                        !this.isFormChatbotEnabled ||
                        value === undefined ||
                        value === null ||
                        Number.isInteger(value)
                    );
                },
                minValue: (value: number | null | undefined): boolean => {
                    return (
                        !this.showChatbotQuotaFields ||
                        !this.isFormChatbotEnabled ||
                        value === undefined ||
                        value === null ||
                        value >= 0
                    );
                },
                maxValue: (value: number | null | undefined): boolean => {
                    return (
                        !this.showChatbotQuotaFields ||
                        !this.isFormChatbotEnabled ||
                        value === undefined ||
                        value === null ||
                        value <= this.chatbotTokenLimitMax
                    );
                },
                usedMinValue: (value: number | null | undefined): boolean => {
                    return (
                        !this.showChatbotQuotaFields ||
                        !this.isFormChatbotEnabled ||
                        value === undefined ||
                        value === null ||
                        value >= this.chatbotTokensUsedMonthly
                    );
                },
            },
        },
    };

    @Emit()
    submit(): ProfileFormData | null {
        const request = {
            firstName: this.formData.firstName,
            lastName: this.formData.lastName,
            phone: this.formData.phone,
            ...(this.showChatbotQuotaFields
                ? {
                      chatbotTokenLimitMonthly: this.submittedChatbotTokenLimit,
                      chatbotEnabled: this.isFormChatbotEnabled,
                  }
                : {}),
        } as ProfileFormData;
        return request;
    }

    get submittedChatbotTokenLimit(): number {
        return this.isFormChatbotEnabled
            ? this.formData.chatbotTokenLimitMonthly ?? 0
            : this.normalizedProfileResponse.chatbotTokenLimitMonthly ?? 0;
    }

    createComparableProfileData(profile: ProfileFormData): ProfileFormData {
        return {
            firstName: profile.firstName,
            lastName: profile.lastName,
            phone: profile.phone,
            ...(this.showChatbotQuotaFields
                ? {
                      chatbotTokenLimitMonthly:
                          profile.chatbotTokenLimitMonthly ?? 0,
                      chatbotEnabled: profile.chatbotEnabled !== false,
                  }
                : {}),
        };
    }

    onSubmit(): void {
        this.submitted = true;
        this.$v.formData.$touch();
        if (!this.$v.$invalid) {
            this.submit();
            this.submitted = false;
        }
    }
}
